import gsap from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';

import {selectAll} from '../simplerToolkit';

gsap.registerPlugin(ScrollTrigger);

document.addEventListener('DOMContentLoaded', function () {
    const sections = selectAll('section.vertical-scroll');

    if (!sections || sections.length === 0) return;

    gsap.matchMedia().add('(max-width: 1023px)', () => {
        ScrollTrigger.normalizeScroll(true);
        return () => ScrollTrigger.normalizeScroll(false);
    });

    sections.forEach((sectionEl) => {
        initDesktop(sectionEl);

        const mediaQuery = gsap.matchMedia();
        mediaQuery.add('(max-width: 1023px)', () => initMobileStack(sectionEl));
    });
});

function initDesktop(section) {
    const imagesContainer = section.querySelector('.scroller_container .images_container');
    const textList = section.querySelector('.scroller_container .text_container ul');

    if (!imagesContainer || !textList) return;

    const cards = imagesContainer.querySelectorAll('.card');
    const listItems = Array.from(textList.querySelectorAll('li'));

    if (!cards.length || !listItems.length) return;

    const listItemByKey = new Map(
        listItems.map((listItem) => [listItem.getAttribute('data-image'), listItem])
    );

    let currentActiveItem = null;

    const setActive = (key) => {
        const listItem = listItemByKey.get(key);
        if (!listItem || currentActiveItem === listItem) return;

        if (currentActiveItem) currentActiveItem.classList.remove('active');
        listItem.classList.add('active');
        currentActiveItem = listItem;
    };

    const firstKey = listItems[0]?.getAttribute('data-image');
    if (firstKey) setActive(firstKey);

    const observer = new IntersectionObserver(
        (entries) => {
            const visibleEntries = entries.filter(
                (entry) => entry.isIntersecting && entry.intersectionRatio >= 1
            );

            if (!visibleEntries.length) return;

            visibleEntries.sort(
                (entryA, entryB) => entryB.intersectionRect.height - entryA.intersectionRect.height
            );

            const target = visibleEntries[0].target;
            const cardKey = Array.from(target.classList).find((cls) => cls.startsWith('slide-'));

            if (cardKey) setActive(cardKey);
        },
        {threshold: 1}
    );

    cards.forEach((card) => observer.observe(card));
}

function initMobileStack(section) {
    const stackContainer = section.querySelector('.vs-stack');
    if (!stackContainer) return;

    const cards = Array.from(stackContainer.querySelectorAll('.vs-card'));
    if (cards.length < 2) return;

    const PEEK_OFFSET_PX = 15;
    const SCALE_STEP = 0.1;
    const OPACITY_STEP = 0.15;
    const MAX_PREVIEW_CARDS = 2;

    const stackOpacity = (stackPosition) =>
        stackPosition > MAX_PREVIEW_CARDS ? 0 : Math.max(1 - stackPosition * OPACITY_STEP, 0.4);

    gsap.set(cards, {clearProps: 'all'});

    cards.forEach((card, cardIndex) => {
        gsap.set(card, {
            y: cardIndex * PEEK_OFFSET_PX,
            scale: 1 - cardIndex * SCALE_STEP,
            opacity: stackOpacity(cardIndex),
            zIndex: cards.length - cardIndex,
            transformOrigin: 'bottom center',
        });
    });

    const stackTimeline = gsap.timeline();

    for (let activeIndex = 0; activeIndex < cards.length - 1; activeIndex++) {
        stackTimeline.to(
            cards[activeIndex],
            {y: '-100%', duration: 1, ease: 'power2.in'},
            activeIndex
        );
        stackTimeline.to(
            cards[activeIndex],
            {opacity: 0, duration: 0.6, ease: 'power1.in'},
            activeIndex + 0.1
        );

        const remainingCards = cards.slice(activeIndex + 1);
        remainingCards.forEach((card, newStackPosition) => {
            stackTimeline.to(
                card,
                {
                    y: newStackPosition * PEEK_OFFSET_PX,
                    scale: 1 - newStackPosition * SCALE_STEP,
                    opacity: stackOpacity(newStackPosition),
                    zIndex: cards.length - newStackPosition,
                    duration: 1,
                    ease: 'power2.out',
                },
                activeIndex
            );
        });
    }

    let lastEnteredAt = 0;

    const scrollTrigger = ScrollTrigger.create({
        trigger: section,
        pin: true,
        pinSpacing: true,
        scrub: 0.5,
        start: () => {
            const stackOffsetFromSection =
                stackContainer.getBoundingClientRect().top - section.getBoundingClientRect().top;
            return `top+=${stackOffsetFromSection}px top+=80px`;
        },
        end: () => `+=${window.innerHeight * (cards.length - 1)}`,
        animation: stackTimeline,
        invalidateOnRefresh: true,
        onEnter: () => {
            lastEnteredAt = Date.now();
        },
        snap: {
            snapTo: (value, self) => {
                const perCard = 1 / (cards.length - 1);
                const currentCard = Math.round(self.progress / perCard);

                if (Date.now() - lastEnteredAt < 500 && self.progress < perCard * 0.6) {
                    return 0;
                }

                const targetCard = Math.floor(value / perCard + 0.35);
                const clampedCard = Math.max(currentCard - 1, Math.min(currentCard + 1, targetCard));

                return Math.max(0, Math.min(1, clampedCard * perCard));
            },
            duration: {min: 0.25, max: 0.6},
            delay: 0.125,
            ease: 'power2.inOut',
        },
        onLeave: (self) => {
            ScrollTrigger.getScrollFunc(window)(Math.round(self.end));
        },
    });

    return () => {
        scrollTrigger.kill();
        gsap.set(cards, {clearProps: 'all'});
    };
}
